import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import classNames from "classnames";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";

import CartAccordions from "~/components/cart/cart-accordions";
import CartKissesInfo from "~/components/cart/cart-kisses-info";
import CartPickProductSection from "~/components/cart/cart-pick-product-section";
import CartProducts from "~/components/cart/cart-products";
import CartPromoCode from "~/components/cart/cart-promo-code";
import CartSavedForLater from "~/components/cart/cart-saved-for-later";
import CartTotals from "~/components/cart/cart-totals";
import Icon from "~/components/common/icon";
import RichText from "~/components/common/rich-text";
import CustomerCare from "~/components/customer-care/customer-care";
import EditorialEntry from "~/components/editorials/editorial-entry";
import LayoutPage from "~/components/layout/layout-page";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import { usePageTracking } from "~/contexts/page-trackings";
import { useCartProductsHierarchicalData, useCartWithPendingStatus } from "~/hooks/use-cart";
import { cartRecommenderElegibleProduct } from "~/services/algolia-utils";
import styles from "~/styles/pages/cart.module.scss";
import Editorials from "~/types/editorials";
import { isActiveEntry } from "~/utils/editorial-utils";
import ssrUtils from "~/utils/ssr-utils";
import TrackingUtils from "~/utils/tracking-utils";

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function CartPage(props: Props) {
  const { page, ...rest } = props;
  const t = useTranslations();
  const breakpoint = useBreakpoint();
  const { cart } = useCartWithPendingStatus();
  const cartProductsParentsData = useCartProductsHierarchicalData();
  const { isAuthenticated } = useAuthContext();
  const setShoppingCartStale = hooks.useSetServiceStale({ serviceState: state.checkout.shoppingCartState });

  const { isPageTracked } = usePageTracking("cart", { title: props.page?.seoMetadata?.fields.seoTitle });
  const ecomTracked = useRef<boolean>(false);
  useEffect(() => {
    if (!ecomTracked.current && isPageTracked && cartProductsParentsData) {
      //@ts-ignore
      TrackingUtils.track(TrackingUtils.mapEcomCart("view_cart", cart?.ShoppingCart, cartProductsParentsData));
      ecomTracked.current = true;
    }
    //@ts-ignore
  }, [cart?.ShoppingCart, cartProductsParentsData, isPageTracked]);

  const refWrapperCartTotalsOnMobile = useRef<HTMLDivElement>(null);
  const [isSticked, setIsSticked] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => setIsSticked(e.intersectionRatio < 1 && e.intersectionRect.top > 0),
      {
        threshold: [1],
        rootMargin: "0px 0px -9px 0px", //to detect the element in sticky mode
      }
    );

    if (refWrapperCartTotalsOnMobile.current) {
      observer?.observe(refWrapperCartTotalsOnMobile.current);
    }

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refWrapperCartTotalsOnMobile.current]);

  // Refresh the recoil cart state when mounting the component
  useEffect(() => {
    setShoppingCartStale();
  }, [setShoppingCartStale]);

  const isCartAlgoliaRelatedEnabled = useMemo(() => {
    const latestCartProduct = cartRecommenderElegibleProduct(cart);
    return !!latestCartProduct || false;
  }, [cart]);

  return (
    <LayoutPage {...rest} page={page as Editorials.BasicPageTemplate} navColor="gradient" hidePrefooter>
      <>
        <div className={styles.container}>
          <div className={styles.titleContainer}>
            <h1>{t("generic.your_cart", { quantity: (cart as any)?.ShoppingCart?.TotalItems ?? 0 })}</h1>
          </div>
          {props.page?.showInformativeMessage && props.page?.informativeMessage != undefined && (
            <div className={styles.message}>
              <Icon name={"info"} width={24} height={24} />
              <RichText text={props.page.informativeMessage} />
            </div>
          )}

          <div className={styles.column1}>
            {breakpoint === "mobile" && (
              <>
                {props.page?.showHighlightedMessage && props.page?.highlightedMessage != undefined && (
                  <div className={styles.customDutiesContainer}>
                    <p className={styles.customDutiesText}>
                      <RichText text={props.page.highlightedMessage} />
                    </p>
                  </div>
                )}
                <CartKissesInfo page={page} />
              </>
            )}
            <CartProducts emptyLinks={page?.emptyLinks} trackingScope="cart" />
            <CartPickProductSection />
            {isAuthenticated && <CartSavedForLater />}
          </div>
          <div className={styles.column2}>
            {!!(cart as any)?.ShoppingCart?.TotalLineCount && (
              <>
                {breakpoint === "desktop" && (
                  <>
                    {props.page?.showHighlightedMessage && props.page?.highlightedMessage != undefined && (
                      <div className={styles.customDutiesContainer}>
                        <p className={styles.customDutiesText}>
                          <RichText text={props.page.highlightedMessage} />
                        </p>
                      </div>
                    )}
                    <CartKissesInfo page={page} />
                  </>
                )}
                <CartPromoCode />
              </>
            )}
            {breakpoint === "desktop" ? (
              <>
                <CartTotals />
                <CustomerCare />
                <CartAccordions page={page} />
              </>
            ) : null}
          </div>
          {breakpoint === "mobile" ? (
            <>
              <div
                className={classNames(styles.cartTotalsSticky, isSticked ? styles.triggerSticky : undefined)}
                ref={refWrapperCartTotalsOnMobile}
              >
                <CartTotals refWrapperCartTotalsOnMobile={refWrapperCartTotalsOnMobile} isSticked={isSticked} />
              </div>
              <div className={styles.column2}>
                <CustomerCare />
                <CartAccordions page={page} />
              </div>
            </>
          ) : null}
        </div>
        {isCartAlgoliaRelatedEnabled
          ? page?.fullCartComponents?.filter(isActiveEntry).map((entry) => {
              return <EditorialEntry key={entry.sys.id} entry={entry} />;
            })
          : page?.emptyCartComponents?.filter(isActiveEntry).map((entry) => {
              return <EditorialEntry key={entry.sys.id} entry={entry} />;
            })}
      </>
    </LayoutPage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  if (context.locale === context.defaultLocale) {
    return {
      notFound: true,
    };
  }

  const pageData = await ssrUtils.getServerSideCartPageData(context);
  return {
    props: {
      ...pageData,
    },
  };
}
